import type { DispensingLabel } from './types';

/**
 * RL-1301: configurable label layout. Sizes are TEST defaults (70 x 40 mm,
 * Auni 20 Oct) until the clinic decides the printer, paper and template
 * (production readiness RL-1503 / RL-1504). Nothing required is ever dropped:
 * text that does not fit moves to a continuation label.
 */
export interface LabelLayout {
  id: string;
  widthMm: number;
  heightMm: number;
  paddingMm: number;
  fontPt: number;
  qrMm: number;
}

export const DEFAULT_LAYOUT: LabelLayout = {
  id: '70x40',
  widthMm: 70,
  heightMm: 40,
  paddingMm: 2,
  fontPt: 6.5,
  qrMm: 14,
};

export const LABEL_LAYOUTS: readonly LabelLayout[] = [
  DEFAULT_LAYOUT,
  {
    id: '100x60',
    widthMm: 100,
    heightMm: 60,
    paddingMm: 3,
    fontPt: 8,
    qrMm: 18,
  },
  {
    id: '50x30',
    widthMm: 50,
    heightMm: 30,
    paddingMm: 1.5,
    fontPt: 5.5,
    qrMm: 11,
  },
];

export function layoutById(id: string | null | undefined): LabelLayout {
  return LABEL_LAYOUTS.find((layout) => layout.id === id) ?? DEFAULT_LAYOUT;
}

/**
 * External-use warning wording per language. English comes from the app
 * strings. Malay, Chinese and Tamil stay empty until the clinic signs the
 * wording off (RL-1502): nothing is invented here. Outside production a
 * missing language shows a visibly marked TEST placeholder; production never
 * shows a placeholder, it simply has no line for that language.
 */
export type ExternalUseLang = 'ms' | 'zh' | 'ta';
export const EXTERNAL_USE_LANGS: readonly ExternalUseLang[] = [
  'ms',
  'zh',
  'ta',
];
export const APPROVED_EXTERNAL_USE_WORDING: Partial<
  Record<ExternalUseLang, string>
> = {};

export interface ExternalUseLine {
  lang: ExternalUseLang;
  /** Approved wording, or null for a TEST placeholder. */
  text: string | null;
}

/**
 * Printed rows of extra external-use wording: one row per approved language,
 * and ONE row for all the languages still waiting for approval (a TEST
 * placeholder, only when `testMode`), so the placeholders cost one line.
 */
export interface ExternalUseRow {
  langs: ExternalUseLang[];
  /** Approved wording, or null for the TEST placeholder row. */
  text: string | null;
}

export function externalUseRows(testMode: boolean): ExternalUseRow[] {
  const rows: ExternalUseRow[] = [];
  const missing: ExternalUseLang[] = [];
  for (const lang of EXTERNAL_USE_LANGS) {
    const approved = APPROVED_EXTERNAL_USE_WORDING[lang];
    if (approved) rows.push({ langs: [lang], text: approved });
    else missing.push(lang);
  }
  if (testMode && missing.length > 0) rows.push({ langs: missing, text: null });
  return rows;
}

/** The extra external-use lines for a label. Placeholders only when `testMode`. */
export function externalUseLines(testMode: boolean): ExternalUseLine[] {
  const lines: ExternalUseLine[] = [];
  for (const lang of EXTERNAL_USE_LANGS) {
    const approved = APPROVED_EXTERNAL_USE_WORDING[lang];
    if (approved) lines.push({ lang, text: approved });
    else if (testMode) lines.push({ lang, text: null });
  }
  return lines;
}

// ---------------------------------------------------------------- pagination

export type FlowKey =
  'directions' | 'directionsBm' | 'warnings' | 'storage' | 'meta';

export interface FlowSection {
  key: FlowKey;
  text: string;
}

export interface LabelPage {
  index: number;
  total: number;
  /** The first page carries the clinic header, pictogram and the leaflet QR. */
  first: boolean;
  sections: FlowSection[];
}

const PT_MM = 0.3528;
/** Average glyph width as a fraction of the font size (conservative). */
const CHAR_WIDTH = 0.52;
const LINE_HEIGHT = 1.22;

/** Text that flows across pages, in print order. Empty values are skipped. */
export function flowOf(
  label: DispensingLabel,
  doctorLine: string,
  batchLine: string | null,
): FlowSection[] {
  const sections: FlowSection[] = [
    { key: 'directions', text: label.directions },
    { key: 'directionsBm', text: label.directionsBm ?? '' },
    { key: 'warnings', text: label.warnings.join(' · ') },
    { key: 'storage', text: label.storage ?? '' },
    {
      key: 'meta',
      text: [doctorLine, batchLine].filter(Boolean).join(' · '),
    },
  ];
  return sections.filter((section) => section.text.trim() !== '');
}

interface Budget {
  charsPerLine: number;
  lines: number;
}

function wrap(text: string, charsPerLine: number): string[] {
  const lines: string[] = [];
  let current = '';
  for (const word of text.trim().split(/\s+/)) {
    // A word longer than a line is split, never dropped.
    for (let rest = word; rest.length > 0;) {
      const room = charsPerLine - (current ? current.length + 1 : 0);
      if (rest.length <= room) {
        current = current ? `${current} ${rest}` : rest;
        rest = '';
      } else if (current) {
        lines.push(current);
        current = '';
      } else {
        lines.push(rest.slice(0, charsPerLine));
        rest = rest.slice(charsPerLine);
      }
    }
  }
  if (current) lines.push(current);
  return lines;
}

function budget(
  layout: LabelLayout,
  reservedLines: number,
  withQr: boolean,
  minLines = 0,
): Budget {
  const width =
    layout.widthMm - 2 * layout.paddingMm - (withQr ? layout.qrMm + 2 : 0);
  const charsPerLine = Math.max(
    10,
    Math.floor(width / (layout.fontPt * PT_MM * CHAR_WIDTH)),
  );
  const lineMm = layout.fontPt * PT_MM * LINE_HEIGHT;
  const total = Math.floor((layout.heightMm - 2 * layout.paddingMm) / lineMm);
  return { charsPerLine, lines: Math.max(minLines, total - reservedLines) };
}

/**
 * Lays the flowing text of one label out on one or more pages of the layout.
 * Words are never dropped or reordered: the sections of all pages, joined per
 * key, are the original text. `bannerLines` (controlled / external-use lines)
 * are reserved on every page. `headerLines` is what the first page spends on
 * the clinic header, medicine, patient line and dose pictogram.
 */
export function paginateLabel(
  flow: FlowSection[],
  layout: LabelLayout,
  options: {
    withQr: boolean;
    bannerLines: number;
    firstHeaderLines: number;
    nextHeaderLines: number;
  },
): LabelPage[] {
  // The first label may carry no text at all when its header and banners
  // already fill the box (the text then starts on the next label); a
  // continuation label always takes at least one line, so the text always ends.
  const first = budget(
    layout,
    options.firstHeaderLines + options.bannerLines,
    options.withQr,
    0,
  );
  const next = budget(
    layout,
    options.nextHeaderLines + options.bannerLines,
    false,
    1,
  );
  const pages: LabelPage[] = [];
  let sections: FlowSection[] = [];
  let left = first.lines;
  let cpl = first.charsPerLine;
  const close = () => {
    pages.push({
      index: pages.length + 1,
      total: 0,
      first: pages.length === 0,
      sections,
    });
    sections = [];
    left = next.lines;
    cpl = next.charsPerLine;
  };
  for (const section of flow) {
    let queue = wrap(section.text, cpl);
    while (queue.length > 0) {
      if (left <= 0) {
        close();
        // Re-wrap what is left at the continuation width.
        queue = wrap(queue.join(' '), cpl);
        continue;
      }
      const take = queue.splice(0, left);
      left -= take.length;
      const last = sections[sections.length - 1];
      if (last?.key === section.key) last.text += ` ${take.join(' ')}`;
      else sections.push({ key: section.key, text: take.join(' ') });
    }
  }
  if (sections.length > 0 || pages.length === 0) close();
  return pages.map((page) => ({ ...page, total: pages.length }));
}

/** Characters that fit on one full-width line of the layout. */
export function charsPerLineOf(layout: LabelLayout, withQr: boolean): number {
  return budget(layout, 0, withQr).charsPerLine;
}

/**
 * Pages of one medicine label. `texts` are the already translated strings the
 * card prints; they only decide how many lines the fixed parts take.
 */
export function planLabel(
  label: DispensingLabel,
  layout: LabelLayout,
  options: {
    withQr: boolean;
    marked: boolean;
    doctorLine: string;
    batchLine: string | null;
    patientLine: string;
    /** Number of external-use lines besides the English one (placeholders count). */
    extraExternalUse: number;
    /**
     * Extra lines kept free on every label. The screen raises it, one line at a
     * time, while the real layout still clips (the estimate is only an estimate).
     */
    slack?: number;
  },
): LabelPage[] {
  const full = charsPerLineOf(layout, false);
  const lines = (text: string, perLine: number) =>
    Math.max(1, Math.ceil(text.length / perLine));
  const clinicText = [
    label.clinic.name,
    label.clinic.address,
    label.clinic.phone,
  ]
    .filter(Boolean)
    .join(' · ');
  const medicine = lines(label.medicine, Math.floor(full * 0.8));
  const patient = lines(options.patientLine, full);
  const band = options.marked ? 1 : 0;
  const banners =
    (label.controlled ? 1 : 0) +
    (label.externalUse ? 1 + options.extraExternalUse : 0) +
    (options.slack ?? 0);
  return paginateLabel(
    flowOf(label, options.doctorLine, options.batchLine),
    layout,
    {
      withQr: options.withQr,
      bannerLines: banners,
      firstHeaderLines:
        band +
        lines(clinicText, Math.floor(full * 1.1)) +
        medicine +
        patient +
        2,
      nextHeaderLines: band + medicine + patient,
    },
  );
}

/** The most extra lines the screen reserves before it gives up and warns. */
export const MAX_SLACK = 8;
